import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";

const SHORT = {
  cross_cultural: "Cross-cultural",
  adaptability: "Adaptability",
  problem_solving: "Problem solving",
  leadership: "Leadership",
  emotional_intelligence: "Emotional Intel.",
  teamwork: "Teamwork",
  independence: "Independence",
  networking: "Networking",
  language: "Language",
};

const FOREST = "#1E293B";
const TERRA = "#2563EB";

export function ResultsCharts({ report }) {
  if (!report) return null;
  const { baseline, post } = report;
  const comps = baseline.competencies;

  const radarData = comps.map((c, i) => ({
    competency: SHORT[c.key] || c.label,
    Baseline: c.pct ?? 0,
    Post: post.competencies[i]?.pct ?? 0,
  }));

  const barData = comps.map((c, i) => ({
    name: SHORT[c.key] || c.label,
    weight: c.weight,
    Baseline: c.contribution ?? 0,
    Post: post.competencies[i]?.contribution ?? 0,
  }));

  return (
    <div className="space-y-8">
      {/* Final scores */}
      <div className="grid gap-4 sm:grid-cols-2">
        <ScoreCard label="Baseline (Before)" data={baseline} accent={FOREST} testid="score-baseline" />
        <ScoreCard label="Post (After)" data={post} accent={TERRA} testid="score-post" />
      </div>

      {/* Radar */}
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-6">
        <h3 className="font-display text-xl text-[#1E293B]">Competency Profile</h3>
        <p className="mb-2 text-sm text-[#1E293B]/60">Normalized score (0–100%) per competency, Baseline vs Post.</p>
        <div style={{ width: "100%", height: 420 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="competency" tick={{ fill: FOREST, fontSize: 11 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#9c968a", fontSize: 10 }} />
              <Radar name="Baseline" dataKey="Baseline" stroke={FOREST} fill={FOREST} fillOpacity={0.25} />
              <Radar name="Post" dataKey="Post" stroke={TERRA} fill={TERRA} fillOpacity={0.4} />
              <Legend />
              <Tooltip formatter={(v) => `${v}%`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Weighted contribution bar */}
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-6">
        <h3 className="font-display text-xl text-[#1E293B]">Weighted Contribution to Final Score</h3>
        <p className="mb-2 text-sm text-[#1E293B]/60">Each competency's points toward the weighted total (out of its weight %).</p>
        <div style={{ width: "100%", height: 360 }}>
          <ResponsiveContainer>
            <BarChart data={barData} margin={{ top: 10, right: 10, left: -10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE3" />
              <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} tick={{ fill: FOREST, fontSize: 11 }} height={70} />
              <YAxis tick={{ fill: "#9c968a", fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Baseline" fill={FOREST} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Post" fill={TERRA} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Numeric table */}
      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
        <div className="border-b border-[#E2E8F0] p-6">
          <h3 className="font-display text-xl text-[#1E293B]">Detailed Numbers</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="results-table">
            <thead>
              <tr className="border-b border-[#E2E8F0] text-left text-xs uppercase tracking-wider text-[#1E293B]/50">
                <th className="p-4">Competency</th>
                <th className="p-4">Weight</th>
                <th className="p-4">Baseline</th>
                <th className="p-4">Post</th>
                <th className="p-4">Change</th>
              </tr>
            </thead>
            <tbody>
              {comps.map((c, i) => {
                const p = post.competencies[i];
                const change = c.pct != null && p?.pct != null ? Math.round((p.pct - c.pct) * 10) / 10 : null;
                return (
                  <tr key={c.key} className="border-b border-[#EAEEF3]" data-testid={`row-${c.key}`}>
                    <td className="p-4 font-medium text-[#1E293B]">{c.label}</td>
                    <td className="p-4 text-[#1E293B]/60">{c.weight}%</td>
                    <td className="p-4">{c.raw != null ? `${c.raw} (${c.pct}%)` : "—"}<div className="text-xs text-[#1E293B]/50">{c.band || ""}</div></td>
                    <td className="p-4">{p?.raw != null ? `${p.raw} (${p.pct}%)` : "—"}<div className="text-xs text-[#1E293B]/50">{p?.band || ""}</div></td>
                    <td className={`p-4 font-semibold ${change == null ? "text-[#1E293B]/40" : change > 0 ? "text-[#15803D]" : change < 0 ? "text-[#2563EB]" : "text-[#1E293B]/60"}`}>
                      {change == null ? "—" : `${change > 0 ? "+" : ""}${change}%`}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#F1F5F9]">
                <td className="p-4 font-display text-base text-[#1E293B]">Weighted Total</td>
                <td className="p-4 text-[#1E293B]/60">100%</td>
                <td className="p-4 font-bold text-[#1E293B]">{baseline.weighted_total}</td>
                <td className="p-4 font-bold text-[#1E293B]">{post.weighted_total}</td>
                <td className="p-4 font-bold text-[#2563EB]">
                  {`${post.weighted_total - baseline.weighted_total > 0 ? "+" : ""}${Math.round((post.weighted_total - baseline.weighted_total) * 10) / 10}`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ScoreCard({ label, data, accent, testid }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">{label}</div>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-display text-5xl" style={{ color: accent }}>{data.weighted_total}</span>
        <span className="mb-1 text-[#1E293B]/50">/ 100</span>
      </div>
      <div className="mt-1 text-sm text-[#1E293B]/60">
        {data.completed_tests}/{data.total_tests} sections completed · {data.weight_covered}% weight scored
      </div>
    </div>
  );
}
